/**
 * UTM + referrer attribution helpers.
 *
 * - First-touch values are written to localStorage on the very first
 *   page load that has UTM params or no record yet. They are NEVER
 *   overwritten.
 * - Last-touch values are updated on every page load to the current
 *   URL/UTM/referrer.
 * - getAttribution() returns the merged first+last touch object that
 *   should be sent with every lead submission.
 */

const FIRST_KEY = "ko_attrib_first_v1";
const LAST_KEY = "ko_attrib_last_v1";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

function readParams() {
  if (typeof window === "undefined") return {};
  const url = new URL(window.location.href);
  const out = {};
  UTM_KEYS.forEach((k) => {
    const v = url.searchParams.get(k);
    if (v) out[k] = v;
  });
  return out;
}

function readJSON(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota errors
  }
}

function buildTouch() {
  if (typeof window === "undefined") return {};
  const params = readParams();
  return {
    utm_source: params.utm_source || null,
    utm_medium: params.utm_medium || null,
    utm_campaign: params.utm_campaign || null,
    utm_term: params.utm_term || null,
    utm_content: params.utm_content || null,
    referrer: (document.referrer || "").slice(0, 500) || null,
    landing_page: window.location.href.slice(0, 500),
    seen_at: new Date().toISOString(),
  };
}

/**
 * Call this once on app mount. It seeds first-touch (if missing) and
 * updates last-touch.
 */
export function captureAttribution() {
  if (typeof window === "undefined") return;

  // First-touch: write only if missing
  const existingFirst = readJSON(FIRST_KEY);
  if (!existingFirst) {
    writeJSON(FIRST_KEY, buildTouch());
  }

  // Last-touch: always refresh on visit
  writeJSON(LAST_KEY, buildTouch());
}

/**
 * Returns the attribution payload to send to the backend.
 * Maps to the backend's flat attribution model.
 */
export function getAttribution() {
  if (typeof window === "undefined") return {};
  const first = readJSON(FIRST_KEY) || buildTouch();
  const last = readJSON(LAST_KEY) || buildTouch();
  return {
    first_utm_source: first.utm_source,
    first_utm_medium: first.utm_medium,
    first_utm_campaign: first.utm_campaign,
    first_utm_term: first.utm_term,
    first_utm_content: first.utm_content,
    first_referrer: first.referrer,
    first_landing_page: first.landing_page,
    first_seen_at: first.seen_at,

    last_utm_source: last.utm_source,
    last_utm_medium: last.utm_medium,
    last_utm_campaign: last.utm_campaign,
    last_utm_term: last.utm_term,
    last_utm_content: last.utm_content,
    last_referrer: last.referrer,
    last_landing_page: last.landing_page,
  };
}

export function getSubmittedPage() {
  if (typeof window === "undefined") return null;
  return window.location.pathname + window.location.search;
}
